import { createEffect, createSignal, onCleanup, onMount } from "solid-js"
import { DatoxComplianceDashboard, DatoxPublishedDashboard } from "./react-dashboard/DatoxComplianceDashboard.jsx"
import type { DatoxDashboard, DatoxDashboardDataset } from "@/datox/api"
import type { ReactNode } from "react"
import type { Root } from "react-dom/client"
import type { ReportExportContext } from "@/datox/report-exports"
import type { ReportDesignerBridge } from "@/datox/report-designer"

type DashboardConfig = Record<string, unknown>

type OutputTable = {
  datasetID: string
  name: string
  headers: string[]
  rows: Array<Record<string, string | number | boolean | null>>
}

export function DatoxDashboardReactHost(props: {
  dashboards: DatoxDashboard[]
  datasets: DatoxDashboardDataset[]
  outputTables: OutputTable[]
  dashboardShareReviewers: Array<{ approvalIds: string[]; name: string; email: string; clientValue?: string; status: string }>
  selectedDashboardID: string
  selectedDatasetID: string
  config: DashboardConfig
  readOnly?: boolean
  saving: boolean
  loading: boolean
  error: string
  labels: Record<string, unknown>
  onCreateDashboard: (name: string) => void
  onDeleteDashboard: () => void
  onPublishDashboard: () => Promise<string>
  onUnpublishDashboard: () => void
  onShareAccess: (reviewers: Array<{ name?: string; email: string; clientValue?: string }>, message?: string) => Promise<string>
  onRemoveShareReviewer: (approvalIds: string[]) => Promise<void>
  onSelectDashboard: (dashboardID: string) => void
  onSelectDataset: (datasetID: string) => void
  onConfigChange: (config: DashboardConfig) => void
  onPrepareExport?: (config: DashboardConfig) => Promise<ReportExportContext>
  onSaveExport?: (context: ReportExportContext, pages: string[], file: Blob) => Promise<void>
  onExportDashboardPdf?: (context: ReportExportContext, html: string, filename: string) => Promise<Blob>
  scoped?: boolean
  autoExport?: boolean
  onAutoExportHandled?: () => void
  studio?: ReportDesignerBridge
  analytics?: { projectID: string; dashboardID: string; revision?: number }
  onExplainSource?: (widgetID: string) => void
  sourceDisabled?: boolean
}) {
  let element: HTMLDivElement | undefined
  let ReactRuntime: { createElement: (component: unknown, props: Record<string, unknown>) => ReactNode } | undefined
  let root: Root | undefined
  const [ready, setReady] = createSignal(false)

  onMount(async () => {
    if (!element) return
    // @ts-ignore React is intentionally provided by the optional dashboard island dependencies.
    ReactRuntime = await import("react")
    // @ts-ignore ReactDOM is intentionally provided by the optional dashboard island dependencies.
    const ReactDomClient = await import("react-dom/client")
    root = ReactDomClient.createRoot(element)
    setReady(true)
  })

  createEffect(() => {
    if (!ready() || !ReactRuntime) return
    root?.render(
      ReactRuntime.createElement(props.readOnly && !props.studio ? DatoxPublishedDashboard : DatoxComplianceDashboard, {
        studio: props.studio,
        analytics: props.analytics,
        onExplainSource: props.onExplainSource,
        sourceDisabled: props.sourceDisabled,
        readOnly: props.readOnly,
        dashboards: props.dashboards,
        datasets: props.datasets,
        outputTables: props.outputTables,
        dashboardShareReviewers: props.dashboardShareReviewers,
        selectedDashboardID: props.selectedDashboardID,
        selectedDatasetID: props.selectedDatasetID,
        config: props.config,
        saving: props.saving,
        loading: props.loading,
        error: props.error,
        labels: props.labels,
        onCreateDashboard: props.onCreateDashboard,
        onDeleteDashboard: props.onDeleteDashboard,
        onPublishDashboard: props.onPublishDashboard,
        onUnpublishDashboard: props.onUnpublishDashboard,
        onShareAccess: props.onShareAccess,
        onRemoveShareReviewer: props.onRemoveShareReviewer,
        onSelectDashboard: props.onSelectDashboard,
        onSelectDataset: props.onSelectDataset,
        onConfigChange: props.onConfigChange,
        onPrepareExport: props.onPrepareExport,
        onSaveExport: props.onSaveExport,
        onExportDashboardPdf: props.onExportDashboardPdf,
        scoped: props.scoped,
        autoExport: props.autoExport,
        onAutoExportHandled: props.onAutoExportHandled,
      }),
    )
  })

  onCleanup(() => root?.unmount())

  return <div ref={(node) => (element = node)} class="h-full min-h-0" />
}
